import { defineComponent as _defineComponent } from 'vue'
import {
  ref, computed, onMounted, getCurrentInstance, onBeforeUnmount,
} from 'vue';

import HtmlImage from '@/components/util/HtmlImage.vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import {
  actionShowError, performDispatchAction, performSaveAction, performValidation,
} from '@/services/vue-utils';
import store from '@/store/store';
import ContactsFilterDialog from '@/components/views/contacts/ContactsFilterDialog.vue';
import { loadStoreModule, tryUnloadStoreModule, unloadStoreModule } from '@/services/module-loader';
import CampaignManagerStore from '@/store/marketing-campaign-manager-store';
import MarketingCampaign from '@/grpc-api/model/marketing-campaign';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import i18n from '@/plugins/i18n';
import useUnsavedGuard from '@/composables/unsaved-guard';
import { useRouter } from 'vue-router/composables';
import { ValidationObserver } from 'vee-validate';
import ContactFilter from '@/grpc-api/model/contact-filter';
import HtmlTemplate, { areHtmlTemplatesSame } from '@/grpc-api/model/html-template';
import nameWithCopyText from '@/util/copy-name';
import toast from '@/plugins/toast-plugin/toast';
import showConfirmationDialog from '@/components/dialogs/confirmation-dialog';
import ContactFilterSelect from '@/components/views/contacts/ContactFilterSelect.vue';
import { ApiErrorCode } from '@/api/api-error';
import PaywallButton from '@/components/buttons/PaywallButton.vue';
import PaywallUpgradeAlert from '@/components/dialogs/PaywallUpgradeAlert.vue';
import NewsletterQuotaBar from './NewsletterQuotaBar.vue';
import NewsletterTemplateEditorDialog from './NewsletterTemplateEditorDialog.vue';
import TemplateGridView from './TemplateGridView.vue';
import { useLanguages } from '../settings/helpers/settings-values';
import EmailPreviewFrame from './EmailPreviewFrame.vue';
import NewsletterTestEmailRecipientsDialog from './NewsletterTestEmailRecipientsDialog.vue';
import ContactsFilterValueRow from '../contacts/ContactsFilterValueRow.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'NewsletterCampaignCompose',
  setup(__props) {

const router = useRouter();

const isNewsletterFeatureAllowed = computed<boolean>(() => store.getters.isNewsletterFeatureAllowed);

const campaignLoaded = ref(false);
const campaign = ref(new MarketingCampaign());
const oldCampaign = ref(campaign.value);
const template = ref(undefined as undefined | HtmlTemplate);
const oldTemplate = ref(undefined as undefined | HtmlTemplate);
const editorTemplate = ref(undefined as undefined | HtmlTemplate);

const isChanged = computed(() => campaignLoaded.value
  && (
    !isModelEqualToModel(oldCampaign.value, campaign.value)
  || (oldTemplate.value !== undefined && template.value === undefined)
  || (oldTemplate.value === undefined && template.value !== undefined)
  || (
    oldTemplate.value !== undefined && template.value !== undefined
    && !areHtmlTemplatesSame(oldTemplate.value, template.value))
  ));
useUnsavedGuard(isChanged);
const canSaveOrSend = computed(() => campaignLoaded.value && template.value?.html !== undefined);

const templates = computed(() => store.getters.marketingCampaignManagerHtmlTemplates as HtmlTemplate[]);
const languages = useLanguages();
const contactFilters = computed(() => store.getters.marketingCampaignManagerContactFilters as ContactFilter[]);

const editedContactFilter = ref(null as null | ContactFilter);
const contactFilterDialogVisible = ref(false);
const editedContactFilterMatchContactCount = ref(null as null | number);
const selectedContactFilterMatchContactCount = ref(null as null | number);

const contactFilterHintText = computed(() => {
  const { value } = selectedContactFilterMatchContactCount;
  return value !== null ? i18n.tc('label.x_recipients', value) : undefined;
});

// template grid actions
const selectTemplate = (t: HtmlTemplate) => {
  editorTemplate.value = t;
};

// campaign template actions
const editCampaignTemplate = () => {
  editorTemplate.value = template.value;
};

const removeCampaignTemplate = () => {
  const action = () => { template.value = undefined; };

  showConfirmationDialog(
    i18n.t('title.newsletter'),
    i18n.t('message.confirm_remove_template'),
    { action },
    {},
  );
};

// campaign actions
const refObserver = ref(null as null | InstanceType<typeof ValidationObserver>);
const savec = async (p = { hideSuccess: false }) => {
  if (!template.value) return false;

  campaign.value.templateName = template.value.name;
  campaign.value.templateDesign = template.value.design;
  campaign.value.templateHtml = template.value.html;
  campaign.value.templatePlaintext = template.value.plaintext;

  let savedCampaign = undefined as MarketingCampaign | undefined;
  const ok = await performSaveAction(
    refObserver.value,
    async () => { savedCampaign = await store.dispatch('updateMarketingCampaign', { campaign: campaign.value }); },
    p,
  );
  if (!ok || !savedCampaign) return false;

  campaign.value = savedCampaign;
  oldCampaign.value = cloneModel(campaign.value);
  oldTemplate.value = cloneModel(template.value);

  return true;
};

const saveCampaign = async () => {
  const ok = await savec();
  if (!ok) return;
  router.replace('/newsletter');
};

const sendCampaign = async () => {
  const valid = await performValidation(refObserver.value);
  if (!valid) return;

  const sendAction = async (omitCampaignCheck: boolean) => {
    const successText = i18n.tc('message.campaign_successfuly_sent');

    try {
      const sent = await performDispatchAction<MarketingCampaign>(
        'sendMarketingCampaign',
        { campaign: campaign.value, omitCampaignCheck },
        { successText, rethrow: true },
      );
      if (sent) router.replace('/newsletter');
    } catch (e: any) {
      if (!omitCampaignCheck && e.code === ApiErrorCode.campaign_manager_similar_campaign_found) {
        showConfirmationDialog(
          campaign.value.title ?? '',
          i18n.t('message.confirm_send_similar_campaign'),
          {
            title: i18n.tc('button.confirm', selectedContactFilterMatchContactCount.value ?? 0),
            action: () => sendAction(true),
          },
          {},
        );
      } else {
        actionShowError(e);
      }
    }
  };

  const action = async () => {
    const saved = await savec({ hideSuccess: true });
    if (saved) sendAction(false);
  };

  showConfirmationDialog(
    campaign.value.title ?? '',
    i18n.t('message.confirm_send_campaign', [selectedContactFilterMatchContactCount.value]),
    { title: i18n.tc('button.send_x_emails', selectedContactFilterMatchContactCount.value ?? 0), action },
    {},
  );
};

const testRecipients = computed(() => store.state.settings.users.map((u) => ({ text: u.name, value: u.email! })) ?? []);

const testRecipientsDialog = ref(false);

const pickTestRecipients = async () => {
  const valid = await performValidation(refObserver.value);
  if (!valid) return;

  testRecipientsDialog.value = true;
};

const testCampaign = async (recipients: string[]) => {
  testRecipientsDialog.value = false;

  // const action = async () => {
  const saved = await savec({ hideSuccess: true });
  if (!saved) return;

  const send = await performDispatchAction('testMarketingCampaign', { campaign: campaign.value, recipients });
  if (!send) return;

  toast.success(i18n.tc('message.message_successfuly_sent'));
  // };
};

const loadCampaign = async () => {
  const route = router.currentRoute;

  let c: MarketingCampaign | undefined | null;

  // valid id route param? -> load campaign
  if (route.params.id !== 'new' && Number(route.params.id) > 0) {
    const id = Number(route.params.id) || 0;
    c = (store.getters.marketingCampaignManagerCampaigns as MarketingCampaign[]).find((o) => o.id === id);

    if (!c) {
      actionShowError(new Error(i18n.tc('error.load_data')));
      router.replace('/newsletter');
    }

    c = await performDispatchAction<MarketingCampaign>('getMarketingCampaign', { campaign: { id } });
    if (!c) {
      router.replace('/newsletter');
    }
  }

  if (c && route.query.action === 'copy') {
    // copy action -> make campaign duplicate
    oldCampaign.value = c;
    campaign.value = cloneModel(c);
    campaign.value.id = 0;
    campaign.value.title = nameWithCopyText(campaign.value.title ?? '', i18n.tc('label.copy_of'));
  } else if (c) {
    // valid campaign
    oldCampaign.value = c;
    campaign.value = cloneModel(c);
  } else {
    // new campaign
    oldCampaign.value = new MarketingCampaign();
    oldCampaign.value.locale = store.state.settings.account.locale;
    campaign.value = cloneModel(oldCampaign.value);
  }

  template.value = new HtmlTemplate({
    name: campaign.value.templateName ?? '',
    design: campaign.value.templateDesign ?? undefined,
    html: campaign.value.templateHtml ?? undefined,
    plaintext: campaign.value.templatePlaintext ?? undefined,
    preview: campaign.value.templatePreview ?? undefined,
  });
  oldTemplate.value = cloneModel(template.value);

  // eslint-disable-next-line no-use-before-define
  selectContactFilter(campaign.value.contactFilter ?? undefined);

  console.log('loadCampaign: ', campaign.value, template.value);
  campaignLoaded.value = true;
};

const saveTemplate = async (t: HtmlTemplate) => {
  template.value = t;
  editorTemplate.value = undefined;
};

// contact filter editor actions
const changedContactFilter = async (contactFilter: ContactFilter) => {
  const action = 'previewContactFilterForMarketingCampaign';
  try {
    const count = await store.dispatch(action, { contactFilter });
    editedContactFilterMatchContactCount.value = count ?? null;
  } catch (e) {
    editedContactFilterMatchContactCount.value = null;
  }
  console.log('changedContactFilter: ', editedContactFilterMatchContactCount.value);
};

const saveContactFilter = async (contactFilter: ContactFilter) => {
  const action = contactFilter.id === 0
    ? 'markeingCampaignManagerCreateContactFilter'
    : 'markeingCampaignManagerUpdateContactFilter';
  const changedFilter = await performDispatchAction<ContactFilter>(action, { contactFilter });
  if (!changedFilter) return;

  contactFilterDialogVisible.value = false;

  campaign.value.contactFilter = changedFilter;
  const res = await performDispatchAction<number>('previewContactFilterForMarketingCampaign', { contactFilter });
  selectedContactFilterMatchContactCount.value = res ?? null;
};

const deleteContactFilter = async (contactFilter: ContactFilter) => {
  const action = 'markeingCampaignManagerDeleteContactFilter';
  const changedFilter = await performDispatchAction<ContactFilter>(action, { contactFilter });
  if (!changedFilter) return;

  contactFilterDialogVisible.value = false;

  campaign.value.contactFilter = null;
  selectedContactFilterMatchContactCount.value = null;
};

// contact filter selector actions
const selectContactFilter = async (contactFilter?: ContactFilter) => {
  selectedContactFilterMatchContactCount.value = null;
  if (contactFilter?.data === undefined) return;

  campaign.value.contactFilter = contactFilter;
  const res = await performDispatchAction<number>('previewContactFilterForMarketingCampaign', { contactFilter });
  selectedContactFilterMatchContactCount.value = res ?? null;
};

const addContactFilter = () => {
  editedContactFilter.value = null;
  contactFilterDialogVisible.value = true;
};

const duplicateContactFilter = () => {
  if (!campaign.value.contactFilter) return;
  editedContactFilter.value = cloneModel(campaign.value.contactFilter);
  editedContactFilter.value.id = 0;
  editedContactFilter.value.name = nameWithCopyText(campaign.value.contactFilter.text, i18n.tc('label.copy_of'));
  contactFilterDialogVisible.value = true;
};

const editContactFilter = () => {
  editedContactFilter.value = campaign.value.contactFilter;
  contactFilterDialogVisible.value = true;
};

// view actions
const load = async () => {
  if (!store.getters.isMarketingCampaignManagerLoaded) {
    await performDispatchAction('loadMarketingCampaignManager', {});
  }
  loadCampaign();
};

const reset = async () => {
  store.dispatch('resetMarketingCampaignManager');
  tryUnloadStoreModule('serviceManager', async () => store.dispatch('resetServiceManager'));
};

onMounted(() => {
  loadStoreModule(
    getCurrentInstance()?.proxy!,
    'marketingCampaignManager',
    CampaignManagerStore,
    async () => { load(); },
  );
});

onBeforeUnmount(() => {
  unloadStoreModule(getCurrentInstance()?.proxy!, 'marketingCampaignManager', async () => { reset(); });
});


return { __sfc: true,router, isNewsletterFeatureAllowed, campaignLoaded, campaign, oldCampaign, template, oldTemplate, editorTemplate, isChanged, canSaveOrSend, templates, languages, contactFilters, editedContactFilter, contactFilterDialogVisible, editedContactFilterMatchContactCount, selectedContactFilterMatchContactCount, contactFilterHintText, selectTemplate, editCampaignTemplate, removeCampaignTemplate, refObserver, savec, saveCampaign, sendCampaign, testRecipients, testRecipientsDialog, pickTestRecipients, testCampaign, loadCampaign, saveTemplate, changedContactFilter, saveContactFilter, deleteContactFilter, selectContactFilter, addContactFilter, duplicateContactFilter, editContactFilter, load, reset, HtmlImage, useSettingsBreadcrumbs, ContactsFilterDialog, ValidationObserver, ContactFilter, ContactFilterSelect, PaywallButton, PaywallUpgradeAlert, NewsletterQuotaBar, NewsletterTemplateEditorDialog, TemplateGridView, EmailPreviewFrame, NewsletterTestEmailRecipientsDialog, ContactsFilterValueRow }
}

})